import type {
  TrendsApiResponse,
  TrendsGraphPoint,
  TrendsLatestMeal,
  TrendsWeeklyBlock,
} from "../services/TrendsService";
import { getLocalDateString } from "./homePageUtils";
import { readApiDistance } from "./fitnessMetrics";

const WEEK_DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;

const emptyWeek = (): TrendsGraphPoint[] =>
  WEEK_DAYS.map((day) => ({ day, value: 0 }));

const num = (v: unknown, fallback = 0): number => {
  if (v == null) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const pick = (obj: Record<string, unknown> | undefined, ...keys: string[]): unknown => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const shortDay = (raw: unknown, index: number): string => {
  const s = String(raw ?? "");
  if (!s) return WEEK_DAYS[index] ?? `${index}`;
  return s.length <= 3 ? s : s.slice(0, 3);
};

export interface TrendsDailyView {
  steps: number;
  steps_goal: number;
  calories_burned: number;
  calories_goal: number;
  active_zone_minutes: number;
  active_zone_minutes_goal: number;
  resting_heart_rate?: number;
  sleep_minutes: number;
  sleep_hours?: string;
  sleep_goal: number;
  weight_kg?: number;
  weight_lbs?: number;
  preferred_weight_unit?: string;
  /** Distance in meters (health-sync `distance` field). */
  distance_m?: number;
  preferred_distance_unit?: string;
  hrv_rmssd?: number;
  spo2_percent?: number;
  breathing_rate?: number;
  skin_temp_celsius?: number;
}

export interface TrendsGlucoseMeta {
  spikeMgDl: number;
  peakMgDl?: number;
  startMgDl?: number;
  category?: string;
  title?: string;
  uiMessage?: string;
  color?: string;
  timeToPeakMin?: number;
}

export interface TrendsPageViewModel {
  hasData: boolean;
  dateLabel: string;
  daily: TrendsDailyView;
  latestMeal?: TrendsLatestMeal;
  glucoseMeta: TrendsGlucoseMeta;
  glucoseCurveData: number[];
  glucoseCurveLabels: string[];
  glucoseCurvePoints: { time_min: number; glucose_mg_dl: number }[];
  stepsBarData: TrendsGraphPoint[];
  calBarData: TrendsGraphPoint[];
  sleepBarData: TrendsGraphPoint[];
  hrvLineData: number[];
  bbsTrend: TrendsGraphPoint[];
  bbsLabel?: string;
  weightBarData: TrendsGraphPoint[];
  weeklyStepAvg: number;
  weeklyStepTotal: number;
  weeklyCalAvg: number;
  latestGlucose: number;
  latestBbs: number;
  latestWeight: number | null;
}

const emptyGlucoseMeta = (): TrendsGlucoseMeta => ({ spikeMgDl: 0 });

export const emptyTrendsViewModel = (): TrendsPageViewModel => ({
  hasData: false,
  dateLabel: new Date().toISOString().split("T")[0],
  daily: {
    steps: 0,
    steps_goal: 10000,
    calories_burned: 0,
    calories_goal: 1577,
    active_zone_minutes: 0,
    active_zone_minutes_goal: 30,
    sleep_minutes: 0,
    sleep_hours: "0h 0 min",
    sleep_goal: 8,
  },
  glucoseMeta: emptyGlucoseMeta(),
  glucoseCurveData: [],
  glucoseCurveLabels: [],
  glucoseCurvePoints: [],
  stepsBarData: [],
  calBarData: [],
  sleepBarData: [],
  hrvLineData: [],
  bbsTrend: [],
  weightBarData: [],
  weeklyStepAvg: 0,
  weeklyStepTotal: 0,
  weeklyCalAvg: 0,
  latestGlucose: 0,
  latestBbs: 0,
  latestWeight: null,
});

function normalizeGraph(raw: unknown, fallback = emptyWeek()): TrendsGraphPoint[] {
  if (raw == null) return fallback;

  if (Array.isArray(raw)) {
    if (raw.length === 0) return fallback;
    return raw.map((item, i) => {
      if (typeof item === "number") {
        return { day: WEEK_DAYS[i] ?? `${i}`, value: item };
      }
      const row = item as Record<string, unknown>;
      const rawValue =
        row.value ??
        row.daily_bbs ??
        row.steps ??
        row.calories ??
        row.calories_burned ??
        row.hours ??
        row.sleep_hours ??
        row.spike ??
        row.bbs ??
        row.score;
      return {
        day: shortDay(row.day ?? row.label ?? row.date, i),
        value: rawValue == null ? 0 : num(rawValue),
      };
    });
  }

  const block = raw as TrendsWeeklyBlock & Record<string, unknown>;
  if (Array.isArray(block.graph)) return normalizeGraph(block.graph, fallback);
  if (Array.isArray(block.points)) return normalizeGraph(block.points, fallback);
  if (Array.isArray(block.this_week)) return normalizeGraph(block.this_week, fallback);
  if (Array.isArray(block.days)) {
    return normalizeGraph(
      block.days.map((day) => ({
        day: (day as Record<string, unknown>).day,
        value:
          (day as Record<string, unknown>).value ??
          (day as Record<string, unknown>).daily_bbs,
      })),
      fallback
    );
  }
  return fallback;
}

function isWeeklyBlock(value: unknown): value is TrendsWeeklyBlock {
  if (!value || typeof value !== "object") return false;
  const block = value as TrendsWeeklyBlock;
  return (
    Array.isArray(block.graph) ||
    Array.isArray(block.points) ||
    Array.isArray(block.this_week) ||
    Array.isArray(block.days)
  );
}

function weeklyBlock(...paths: unknown[]): TrendsWeeklyBlock | undefined {
  for (const p of paths) {
    if (isWeeklyBlock(p)) return p;
  }
  return undefined;
}

function weeklyAvg(graph: TrendsGraphPoint[], block?: TrendsWeeklyBlock): number {
  const fromApi = block?.daily_avg ?? block?.weekly_average;
  if (fromApi != null && Number.isFinite(Number(fromApi))) return Math.round(Number(fromApi));
  const withValues = graph.filter((d) => d.value > 0);
  if (!withValues.length) return 0;
  return Math.round(withValues.reduce((s, d) => s + d.value, 0) / withValues.length);
}

function weeklyTotal(graph: TrendsGraphPoint[], block?: TrendsWeeklyBlock): number {
  if (block?.weekly_total != null && Number.isFinite(Number(block.weekly_total))) {
    return Math.round(Number(block.weekly_total));
  }
  return Math.round(graph.reduce((sum, d) => sum + d.value, 0));
}

function formatSleepHours(minutes: number): string {
  if (minutes <= 0) return "0h 0 min";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m} min`;
}

function mapGlucoseCurve(api: TrendsApiResponse | null | undefined): {
  data: number[];
  labels: string[];
  points: { time_min: number; glucose_mg_dl: number }[];
  meta: TrendsGlucoseMeta;
} {
  const g = api?.graph;
  const rawPoints = g?.curve?.points;
  const points = Array.isArray(rawPoints) ? rawPoints : [];
  const data = points.map((p) => num(p.glucose_mg_dl));
  const labels = points.map((p) => `${Math.round(num(p.time_min))}m`);
  const mappedPoints = points.map((p) => ({ time_min: num(p.time_min), glucose_mg_dl: num(p.glucose_mg_dl) }));

  return {
    data,
    labels,
    points: mappedPoints,
    meta: {
      spikeMgDl: num(g?.spike_mg_dl),
      peakMgDl: num(g?.peak_mg_dl) || undefined,
      startMgDl: num(g?.start_mg_dl) || undefined,
      category: g?.spike_category,
      title: g?.title,
      uiMessage: g?.ui_message,
      color: g?.color,
      timeToPeakMin: num(g?.time_to_peak_min) || undefined,
    },
  };
}

function mapBbsTrendFromApi(api: TrendsApiResponse | null | undefined): TrendsGraphPoint[] {
  const graph = api?.bbs_trend?.graph;
  if (Array.isArray(graph) && graph.length > 0) {
    return graph.map((item, i) => ({
      day: shortDay(item.day ?? item.date, i),
      value: num(item.value),
    }));
  }

  const bbsBlock = weeklyBlock(api?.bbs_trend, api?.nutrition?.bbs_trend);
  return normalizeGraph(bbsBlock?.graph ?? bbsBlock);
}

function mapLatestBbs(api: TrendsApiResponse | null | undefined): number {
  const selectedDate = api?.date_local ?? getLocalDateString();

  const graph = api?.bbs_trend?.graph;
  if (Array.isArray(graph) && graph.length > 0 && selectedDate) {
    const selectedPoint = graph.find((p) => p.date === selectedDate);
    if (selectedPoint) {
      return selectedPoint.value != null ? num(selectedPoint.value) : 0;
    }
  }

  const selectedDay =
    api?.bbs_trend?.days?.find((d) => d.date === selectedDate) ??
    (selectedDate === (api?.today_date_local ?? getLocalDateString())
      ? api?.bbs_trend?.days?.find((d) => d.is_today)
      : undefined);
  if (selectedDay) {
    const raw = selectedDay.value ?? selectedDay.daily_bbs;
    return raw != null ? num(raw) : 0;
  }

  const dailyBbs = api?.["Bite Balance Score"]?.daily_bbs;
  if (dailyBbs != null) return num(dailyBbs);

  return 0;
}

function mapBbsLabel(api: TrendsApiResponse | null | undefined): string | undefined {
  const selectedDate = api?.date_local ?? getLocalDateString();
  const selectedDay =
    api?.bbs_trend?.days?.find((d) => d.date === selectedDate) ??
    (selectedDate === (api?.today_date_local ?? getLocalDateString())
      ? api?.bbs_trend?.days?.find((d) => d.is_today)
      : undefined);
  if (selectedDay?.bbs_label) return selectedDay.bbs_label;
  return api?.["Bite Balance Score"]?.label;
}


function parseOptionalWeight(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const n = num(value, 0);
  return n > 0 ? n : undefined;
}

/** All weight values from API — show kg and/or lbs when present (no unit conversion). */
export function getTrendsWeightDisplayParts(
  daily: TrendsDailyView,
  fallback?: number | null
): Array<{ value: number; unit: "kg" | "lbs" }> {
  const parts: Array<{ value: number; unit: "kg" | "lbs" }> = [];
  if (daily.weight_kg != null) {
    parts.push({ value: daily.weight_kg, unit: "kg" });
  }
  if (daily.weight_lbs != null) {
    parts.push({ value: daily.weight_lbs, unit: "lbs" });
  }
  if (parts.length === 0 && fallback != null && fallback > 0) {
    parts.push({ value: fallback, unit: "kg" });
  }
  return parts;
}

function formatMealClockTime(timeStr: string): string | undefined {
  const trimmed = timeStr.trim();
  if (!trimmed) return undefined;
  if (/am|pm/i.test(trimmed)) return trimmed;

  const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return trimmed;

  let h = Number.parseInt(match[1], 10);
  const m = match[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

function formatMealDateLabel(mealTimeLocal?: string): string | undefined {
  const datePart = mealTimeLocal?.trim().split(/\s+/)[0];
  if (!datePart) return undefined;
  const d = new Date(`${datePart}T12:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Meal type, title, date and time from trends latest_meal. */
export function formatTrendsLatestMealMeta(meal?: TrendsLatestMeal): string | undefined {
  if (!meal) return undefined;

  const parts: string[] = [];
  const type = meal.meal_type?.trim();
  const title = meal.meal_title?.trim();
  if (type) parts.push(type);
  if (title) parts.push(title);

  const dateLabel = formatMealDateLabel(meal.meal_time_local);
  const timeLabel =
    meal.meal_time_display?.trim() ||
    (meal.meal_time ? formatMealClockTime(meal.meal_time) : undefined) ||
    (meal.meal_time_local
      ? formatMealClockTime(meal.meal_time_local.split(/\s+/).pop() ?? "")
      : undefined);

  if (dateLabel) parts.push(dateLabel);
  if (timeLabel) parts.push(timeLabel);

  return parts.length ? parts.join(" · ") : undefined;
}

export const mapTrendsApiToViewModel = (
  api: TrendsApiResponse | null | undefined
): TrendsPageViewModel => {
  if (!api || typeof api !== "object") {
    return emptyTrendsViewModel();
  }
  // Prefer the request date (date_local). Never use UTC ISO day — that can lag a day in late timezones.
  const today = api?.date_local ?? api?.today_date_local ?? getLocalDateString();
  const overview =
    (api?.today_overview as Record<string, unknown> | undefined) ??
    (api?.["Today's Overview"] as Record<string, unknown> | undefined) ??
    (api?.["Today's Activity"] as Record<string, unknown> | undefined);

  const sleepFromActivity = pick(overview, "Sleep");
  const sleepMinutesRaw = pick(overview, "sleep_minutes", "sleep") ?? api?.sleep_minutes;
  let sleepMinutes = num(sleepMinutesRaw);
  if (sleepMinutes <= 0 && sleepFromActivity != null) {
    sleepMinutes = Math.round(num(sleepFromActivity) * 60);
  }
  const sleepGoal = num(pick(overview, "sleep_goal", "sleep_goal_hours") ?? api?.sleep_goal, 8);

  const weightKgRaw = api?.weight?.weight_kg ?? api?.weight_kg;
  const weightLbsRaw = api?.weight?.weight_lbs ?? api?.weight_lbs;
  const weightUnit =
    (api?.weight?.unit as string | undefined) ??
    (api?.preferred_weight_unit as string | undefined) ??
    "kg";
  const weightKg = parseOptionalWeight(weightKgRaw);
  const weightLbs = parseOptionalWeight(weightLbsRaw);

  const daily: TrendsDailyView = {
    steps: num(pick(overview, "steps", "Total Steps") ?? api?.steps),
    steps_goal: num(pick(overview, "steps_goal") ?? api?.steps_goal, 10000),
    calories_burned: num(
      pick(overview, "calories_burned", "Calories Burned") ?? api?.calories_burned
    ),
    calories_goal: num(pick(overview, "calories_goal") ?? api?.calories_goal, 1577),
    active_zone_minutes: num(
      pick(overview, "active_zone_minutes", "Active Minutes", "active_zone_minutes") ??
        api?.active_zone_minutes
    ),
    active_zone_minutes_goal: num(
      pick(overview, "active_zone_minutes_goal") ?? api?.active_zone_minutes_goal,
      30
    ),
    resting_heart_rate: num(
      pick(overview, "resting_heart_rate", "BPM", "resting_heart_rate") ??
        api?.resting_heart_rate,
      0
    ) || undefined,
    sleep_minutes: sleepMinutes,
    sleep_hours:
      (pick(overview, "sleep_hours", "Sleep UI") as string | undefined) ??
      api?.sleep_hours ??
      formatSleepHours(sleepMinutes),
    sleep_goal: sleepGoal,
    weight_kg: weightKg,
    weight_lbs: weightLbs,
    preferred_weight_unit: weightUnit,
    // API distance (meters) — pass through unchanged.
    distance_m: readApiDistance(
      overview,
      api?.care as Record<string, unknown> | undefined,
      api as Record<string, unknown>,
    ),
    preferred_distance_unit:
      (pick(api?.care as Record<string, unknown>, "preferred_distance_unit") as
        | string
        | undefined) ?? (api?.preferred_distance_unit as string | undefined) ?? "m",
    // HRV / heart rate variability from health-sync vitals or trends care block.
    hrv_rmssd: (() => {
      const vitals = api?.learning?.vitals as Record<string, unknown> | undefined;
      const learningHrv = api?.learning?.hrv as Record<string, unknown> | undefined;
      const care = api?.care as Record<string, unknown> | undefined;
      const raw =
        pick(learningHrv, "current", "current_value", "value") ??
        pick(overview, "hrv_rmssd", "heart_rate_variability", "hrv") ??
        pick(vitals, "hrv_rmssd", "heart_rate_variability", "hrv") ??
        pick(care, "hrv_rmssd", "heart_rate_variability", "hrv") ??
        api?.hrv_rmssd;
      if (raw == null || raw === "") return undefined;
      if (typeof raw === "object" && !Array.isArray(raw)) {
        const nested = (raw as Record<string, unknown>).value;
        const n = Number(nested);
        return Number.isFinite(n) ? n : undefined;
      }
      const n = Number(raw);
      return Number.isFinite(n) ? n : undefined;
    })(),
    spo2_percent: num(
      pick(api?.learning?.vitals, "spo2_percent", "spo2") ??
        pick(overview, "spo2_percent") ??
        pick(api?.care as Record<string, unknown>, "spo2_percent") ??
        api?.spo2_percent,
      0
    ) || undefined,
    breathing_rate: num(
      pick(api?.learning?.vitals, "breathing_rate") ??
        pick(overview, "breathing_rate") ??
        pick(api?.care as Record<string, unknown>, "breathing_rate") ??
        api?.breathing_rate,
      0
    ) || undefined,
    skin_temp_celsius: num(
      pick(api?.learning?.vitals, "skin_temp_celsius", "skin_temp") ??
        pick(overview, "skin_temp_celsius") ??
        pick(api?.care as Record<string, unknown>, "skin_temp_celsius") ??
        api?.skin_temp_celsius,
      0
    ) || undefined,
  };

  const stepsBlock = weeklyBlock(api?.progress?.weekly_steps, api?.weekly_steps);
  const calBlock = weeklyBlock(
    api?.progress?.weekly_calories_burned,
    api?.progress?.weekly_calories,
    api?.weekly_calories_burned,
    api?.["Weekly Calorie Burned Trend"]
  );
  const sleepBlock = weeklyBlock(
    api?.progress?.weekly_sleep,
    api?.weekly_sleep,
    api?.["Weekly Sleep Trend"]
  );
  const hrvBlock = weeklyBlock(api?.learning?.hrv, api?.hrv);
  const weightBlock = weeklyBlock(api?.care?.weekly_weight, api?.care);

  const stepsBarData = normalizeGraph(stepsBlock?.graph ?? stepsBlock);
  const calBarData = normalizeGraph(calBlock?.graph ?? calBlock);
  const sleepBarData = normalizeGraph(sleepBlock?.graph ?? sleepBlock);
  const hrvLineData = normalizeGraph(hrvBlock?.graph ?? hrvBlock).map((d) => d.value);
  const bbsTrend = mapBbsTrendFromApi(api);
  const weightBarData = normalizeGraph(
    (weightBlock as TrendsWeeklyBlock | undefined)?.graph ?? weightBlock
  );

  const { data: glucoseCurveData, labels: glucoseCurveLabels, points: glucoseCurvePoints, meta: glucoseMeta } =
    mapGlucoseCurve(api);

  const latestGlucose = glucoseMeta.spikeMgDl;
  const latestBbs = mapLatestBbs(api);
  const bbsLabel = mapBbsLabel(api);
  const latestWeight =
    weightBarData.length > 0 ? weightBarData[weightBarData.length - 1].value : null;

  return {
    hasData: Boolean(api?.has_data),
    dateLabel: today,
    daily,
    latestMeal: api?.latest_meal,
    glucoseMeta,
    glucoseCurveData,
    glucoseCurveLabels,
    glucoseCurvePoints,
    stepsBarData,
    calBarData,
    sleepBarData,
    hrvLineData,
    bbsTrend,
    bbsLabel,
    weightBarData,
    weeklyStepAvg: weeklyAvg(stepsBarData, stepsBlock),
    weeklyStepTotal: weeklyTotal(stepsBarData, stepsBlock),
    weeklyCalAvg: weeklyAvg(calBarData, calBlock),
    latestGlucose,
    latestBbs,
    latestWeight,
  };
};
